/**
 * OPTIMIZADOR DE PERFORMANCE PARA REDUCIR INP
 * Cargar ANTES de main.js en theme.liquid
 */

// ============================================
// 1. OPTIMIZAR EVENT LISTENERS GLOBALES
// ============================================
(function() {
  'use strict';
  
  // Interceptar addEventListener para hacerlo passive por defecto
  const originalAddEventListener = EventTarget.prototype.addEventListener;
  EventTarget.prototype.addEventListener = function(type, listener, options) {
    const passiveEvents = ['scroll', 'wheel', 'touchstart', 'touchmove', 'touchenter', 'touchend', 'touchleave'];
    
    if (passiveEvents.includes(type) && typeof options !== 'object') {
      options = { passive: true };
    } else if (typeof options === 'object' && options.passive === undefined) {
      options.passive = passiveEvents.includes(type);
    }
    
    return originalAddEventListener.call(this, type, listener, options);
  };
})();

// ============================================
// 2. DEBOUNCE MEJORADO PARA INPUTS
// ============================================
const debounceMap = new WeakMap();

document.addEventListener('input', function(e) {
  const target = e.target;
  if (!target.matches('input, textarea, select')) return;
  
  let timeout = debounceMap.get(target);
  if (timeout) clearTimeout(timeout);
  
  timeout = setTimeout(() => {
    target.dispatchEvent(new Event('debounced-input', { bubbles: true }));
  }, 400); // Aumentado para mejor INP
  
  debounceMap.set(target, timeout);
}, true);

// ============================================
// 3. LAZY LOADING DE IMÁGENES
// ============================================
if ('loading' in HTMLImageElement.prototype) {
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('img[data-src]').forEach(img => {
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    });
  });
} else {
  // Fallback para navegadores antiguos
  const imageObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
        observer.unobserve(img);
      }
    });
  }, { rootMargin: '50px' });
  
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img);
    });
  });
}

// ============================================
// 4. OPTIMIZAR FETCH/AJAX
// ============================================
const fetchCache = new Map();
const originalFetch = window.fetch;

window.fetch = function(url, options = {}) {
  // Cache para GET requests
  if (!options.method || options.method === 'GET') {
    const cacheKey = typeof url === 'string' ? url : url.url;
    
    if (fetchCache.has(cacheKey)) {
      const cached = fetchCache.get(cacheKey);
      if (Date.now() - cached.timestamp < 5000) { // Cache por 5 segundos
        return Promise.resolve(cached.response.clone());
      }
    }
  }
  
  return originalFetch.apply(this, arguments).then(response => {
    if (response.ok && (!options.method || options.method === 'GET')) {
      const cacheKey = typeof url === 'string' ? url : url.url;
      fetchCache.set(cacheKey, {
        response: response.clone(),
        timestamp: Date.now()
      });
    }
    return response;
  });
};

// ============================================
// 5. BATCH DOM OPERATIONS
// ============================================
window.batchDOM = (() => {
  let readQueue = [];
  let writeQueue = [];
  let scheduled = false;
  
  const flush = () => {
    // Primero todas las lecturas
    readQueue.forEach(fn => fn());
    readQueue = [];
    
    // Luego todas las escrituras
    writeQueue.forEach(fn => fn());
    writeQueue = [];
    
    scheduled = false;
  };
  
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(flush);
  };
  
  return {
    read(fn) {
      readQueue.push(fn);
      schedule();
    },
    write(fn) {
      writeQueue.push(fn);
      schedule();
    }
  };
})();

// ============================================
// 6. OPTIMIZAR BÚSQUEDA PREDICTIVA
// ============================================
let searchController;
let searchTimeout;

document.addEventListener('input', function(e) {
  if (!e.target.matches('[name*="search"], [type="search"]')) return;
  
  clearTimeout(searchTimeout);
  if (searchController) searchController.abort();
  
  searchTimeout = setTimeout(() => {
    const query = e.target.value.trim();
    if (query.length < 2) return;
    
    searchController = new AbortController();
    // El fetch original lo manejará el código principal
  }, 300);
}, true);

// ============================================
// 7. REDUCIR LAYOUT THRASHING
// ============================================
const measurementCache = new WeakMap();

['offsetWidth', 'offsetHeight', 'clientWidth', 'clientHeight', 'scrollWidth', 'scrollHeight'].forEach(prop => {
  Object.defineProperty(HTMLElement.prototype, `_cached_${prop}`, {
    get() {
      const cache = measurementCache.get(this) || {};
      if (cache[prop] && Date.now() - cache[`${prop}_time`] < 100) {
        return cache[prop];
      }
      
      const value = this[prop];
      cache[prop] = value;
      cache[`${prop}_time`] = Date.now();
      measurementCache.set(this, cache);
      
      return value;
    }
  });
});

// ============================================
// 8. OPTIMIZAR CARRITO
// ============================================
let cartUpdateQueue = [];
let cartUpdateScheduled = false;

window.optimizedCartUpdate = function(updates) {
  cartUpdateQueue.push(...(Array.isArray(updates) ? updates : [updates]));
  
  if (!cartUpdateScheduled) {
    cartUpdateScheduled = true;
    
    requestIdleCallback(() => {
      const batch = [...cartUpdateQueue];
      cartUpdateQueue = [];
      cartUpdateScheduled = false;
      
      // Procesar en lote
      processBatchCartUpdates(batch);
    }, { timeout: 100 });
  }
};

function processBatchCartUpdates(updates) {
  // Agrupar por tipo de operación
  const grouped = updates.reduce((acc, update) => {
    const key = update.type || 'default';
    if (!acc[key]) acc[key] = [];
    acc[key].push(update);
    return acc;
  }, {});
  
  // Procesar cada grupo
  Object.values(grouped).forEach(group => {
    // El código original del carrito procesará esto
    if (window.cartUpdateHandler) {
      window.cartUpdateHandler(group);
    }
  });
}

// ============================================
// 9. PRELOAD CRITICAL RESOURCES
// ============================================
document.addEventListener('DOMContentLoaded', () => {
  // Preconnect a dominios externos
  ['fonts.googleapis.com', 'fonts.gstatic.com', 'cdn.shopify.com'].forEach(domain => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = `https://${domain}`;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });
});

// ============================================
// 10. OPTIMIZAR ANIMACIONES
// ============================================
const animationCache = new WeakSet();

const optimizeAnimation = (element) => {
  if (animationCache.has(element)) return;
  animationCache.add(element);
  
  element.style.willChange = 'transform, opacity';
  
  const cleanup = () => {
    element.style.willChange = 'auto';
    element.removeEventListener('transitionend', cleanup);
    element.removeEventListener('animationend', cleanup);
  };
  
  element.addEventListener('transitionend', cleanup, { once: true });
  element.addEventListener('animationend', cleanup, { once: true });
};

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[class*="animate"], [class*="transition"]').forEach(optimizeAnimation);
});

// ============================================
// 11. REDUCIR REFLOWS
// ============================================
const styleCache = new WeakMap();

Object.defineProperty(HTMLElement.prototype, 'cachedStyle', {
  get() {
    let cache = styleCache.get(this);
    if (!cache || Date.now() - cache.timestamp > 100) {
      cache = {
        computed: window.getComputedStyle(this),
        timestamp: Date.now()
      };
      styleCache.set(this, cache);
    }
    return cache.computed;
  }
});

// ============================================
// 12. INTERSECTION OBSERVER PARA LAZY COMPONENTS
// ============================================
const componentObserver = new IntersectionObserver((entries) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      const element = entry.target;
      const componentName = element.dataset.lazyComponent;
      
      if (componentName && window[componentName]) {
        requestIdleCallback(() => {
          window[componentName].init(element);
        });
      }
      
      componentObserver.unobserve(element);
    }
  });
}, { rootMargin: '100px' });

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-lazy-component]').forEach(el => {
    componentObserver.observe(el);
  });
});

// ============================================
// 13. MONITOR DE PERFORMANCE (OPCIONAL)
// ============================================
if (window.PerformanceObserver) {
  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      // Log solo en desarrollo
      if (window.location.hostname === 'localhost' || window.location.search.includes('debug=1')) {
        console.log('INP event:', entry.name, entry.duration.toFixed(2) + 'ms');
      }
    }
  });
  
  try {
    observer.observe({ type: 'event', buffered: true });
  } catch (e) {
    // Navegador no soporta event timing
  }
}

// ============================================
// 14. EXPORTAR UTILIDADES
// ============================================
window.performanceUtils = {
  batchDOM: window.batchDOM,
  optimizedCartUpdate: window.optimizedCartUpdate,
  debounceInput: (callback, delay = 300) => {
    let timeout;
    return function(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => callback.apply(this, args), delay);
    };
  },
  throttle: (callback, delay = 100) => {
    let lastCall = 0;
    return function(...args) {
      const now = Date.now();
      if (now - lastCall >= delay) {
        lastCall = now;
        callback.apply(this, args);
      }
    };
  }
};

console.log('⚡ Performance Optimizer loaded - INP improvements active');
